import dayjs from 'dayjs';
import { FILTERS_CODE_SEPARATOR } from 'core/constants/platform.constants';
import { EBannerTarget, EBannerType } from 'core/model/enums/banners.enum';
import { EApplications } from 'core/model/enums/channel.enum';
import type {
  IBannerCarouselListItem,
  IBannerCarouselListResponse,
  IBannerListResponse,
  IBannerUpsertValues,
  IMapBannerVisualizationFilterValues,
  ISegmentedBannerListItem,
  TBannerListItem,
  TCreateBannerParams,
  TToggleBannerParams,
  TUpdateBannerParams,
} from 'core/model/interfaces/banners';
import { toMapBranchFilterByDistributorFilterCode, toMapPriceListFilterByPriceListFilterCode } from 'shared/utils';
import { formatDate, formatDateTimeToIso, formatLocaleDate, formatLocaleTime } from 'shared/utils/date.utils';
import { clearEmptySpaces } from 'shared/utils/encode.utils';
import { toPriceGroupList } from '../master-entities/master-entities.mappers';
import type {
  IBannerCarouselListItemDTO,
  IGetBannerListParams,
  IGetBannerListResponseDTO,
  IGetSegmentedBannerCarouselListParams,
  IGetSegmentedBannerCarouselListResponseDTO,
  IPublishBannerListItemDTO,
  IPublishSegmentedBannersRequestDTO,
  TCreateBannerParamsDTO,
} from './banners.dto';
import { getBannerByTargetValues, getBannerByTypeValues, toBannerZoneList } from './banners.utils';

export const toMapBannerCarouselListParams = (params: IGetSegmentedBannerCarouselListParams) => {
  const newParams: IMapBannerVisualizationFilterValues = {
    page: params.page,
    itemsPerPage: params.itemsPerPage,
    distributorBranchCodes: '',
    priceListIds: '',
  };

  if (Array.isArray(params.branches) && params.branches.length > 0) {
    const distributorBranchCodes = params.branches.map(item => {
      const [, branchId, distributorId] = item.split(FILTERS_CODE_SEPARATOR);

      const distributorBranchCode = `${branchId}-${distributorId}`;

      return distributorBranchCode;
    });

    newParams.distributorBranchCodes = distributorBranchCodes.join(',');
  } else {
    delete newParams.distributorBranchCodes;
  }

  if (Array.isArray(params.priceListIds) && params.priceListIds.length > 0) {
    const priceListIds = toMapPriceListFilterByPriceListFilterCode(params.priceListIds);

    newParams.priceListIds = priceListIds.join(',');
  } else {
    delete newParams.priceListIds;
  }

  return newParams;
};

export const toBannerCarouselListItem = (banner: IBannerCarouselListItemDTO) => {
  const now = dayjs();

  const bannerStartDate = dayjs(banner.startDatetime);

  const bannerEndDate = dayjs(banner.endDatetime);

  /**
   * Determines if the banner is currently active based on the start and end dates.
   */
  const isCurrent =
    (now.isAfter(bannerStartDate) && now.isBefore(bannerEndDate)) ||
    now.isSame(bannerStartDate, 'day') ||
    now.isSame(bannerEndDate, 'day');

  const carouselBanner: IBannerCarouselListItem = {
    id: banner.id,
    bannerName: banner.name,
    bannerImages: {
      mobileImageUrl: banner.images.mobileUrl,
      desktopImageUrl: banner.images.desktopUrl,
    },
    bannerStartDate: formatDate(banner.startDatetime, 'DD MMM YYYY'),
    bannerEndDate: formatDate(banner.endDatetime, 'DD MMM YYYY'),
    excludedCustomerCodes: Array.isArray(banner.excludedExternalCustomerIds) ? banner.excludedExternalCustomerIds : [],
    isCurrent,
    isActive: banner.isActive,
  };

  return carouselBanner;
};

export const toGetBannerCarouselList = (
  response: IGetSegmentedBannerCarouselListResponseDTO,
): IBannerCarouselListResponse => {
  const list = response.segmentedBanners.map(segmentedBanner => {
    const value: ISegmentedBannerListItem = {
      index: segmentedBanner.index,
      priceGroups: toPriceGroupList(segmentedBanner.priceGroups),
      zones: toBannerZoneList(segmentedBanner.zones),
      banners: segmentedBanner.banners.map(toBannerCarouselListItem),
    };

    return value;
  });

  const result: IBannerCarouselListResponse = {
    segmentedBanners: list,
    pagination: response.pagination,
  };

  return result;
};

/**
 * This functionality is used to remove the target from the params
 * when the target is `EBannerTarget.SEGMENT`, because the business rule
 * is that the backend service returns all banners regardless of the target.
 * This is necessary to avoid sending the target in the request when it is not needed.
 */
export const toMapBannerListByTargetParams = (params: IGetBannerListParams) => {
  const newParams: IGetBannerListParams = {
    ...params,
  };

  if (newParams.target === EBannerTarget.SEGMENT) {
    delete newParams.target;
  }

  return newParams;
};

export const toGetBannerListByTarget = (response: IGetBannerListResponseDTO): IBannerListResponse => {
  const list = response.banners.map(banner => {
    const bannerTypeValues = getBannerByTypeValues(banner);

    const bannerTargetValues = getBannerByTargetValues(banner);

    const formattedBanner: TBannerListItem = {
      id: banner.id,
      bannerName: banner.name,
      bannerImages: {
        mobileImageUrl: banner.images.mobileUrl,
        desktopImageUrl: banner.images.desktopUrl,
      },
      bannerStartDate: formatLocaleDate(banner.startDatetime),
      bannerStartTime: formatLocaleTime(banner.startDatetime),
      bannerEndDate: formatLocaleDate(banner.endDatetime),
      bannerEndTime: formatLocaleTime(banner.endDatetime),
      isActive: banner.isActive,
      ...bannerTypeValues,
      ...bannerTargetValues,
    };

    return formattedBanner;
  });

  const result: IBannerListResponse = {
    banners: list,
    pagination: response.pagination,
  };

  return result;
};

export const toUpsertBanner = (banner: IBannerUpsertValues) => {
  const startDatetime = formatDateTimeToIso(banner.bannerStartDate as Date, banner.bannerStartTime);

  const endDatetime = formatDateTimeToIso(banner.bannerEndDate as Date, banner.bannerEndTime);

  const payload: TCreateBannerParamsDTO = {
    applicationId: `${banner.platform}`,
    name: banner.bannerName,
    typeId: banner.bannerType,
    target: banner.bannerTarget,
    startDatetime,
    endDatetime,
  } as TCreateBannerParamsDTO;

  if (payload.applicationId === `${EApplications.WEB}`) {
    payload.mobileImageFile = banner.mobileImageFile.blob;

    payload.desktopImageFile = banner.desktopImageFile.blob;
  }

  if (payload.applicationId === `${EApplications.APP}`) {
    payload.mobileImageFile = banner.mobileImageFile.blob;
  }

  if (payload.typeId === `${EBannerType.LINK}`) {
    payload.redirectUrl = banner.bannerLinkImageUrl;
  }

  if (payload.typeId === `${EBannerType.PROMOTIONAL}`) {
    const promotionExternalIdList = banner.promotionList.map(promotion => promotion.promotionId);

    payload.promotionExternalIds = promotionExternalIdList.join(',');
  }

  if (payload.typeId === `${EBannerType.POPUP}`) {
    payload.popupImageFile = banner.popupImageFile.blob;
  }

  if (payload.target === `${EBannerTarget.SEGMENT}`) {
    const branches = toMapBranchFilterByDistributorFilterCode(banner.distributorIdList);

    payload.branches = JSON.stringify(branches);

    const priceListIds = toMapPriceListFilterByPriceListFilterCode(banner.priceListIdList);

    payload.priceListIds = priceListIds.join(',');

    if (banner.shouldExcludeCustomers) {
      const excludedCustomerCodes = banner.excludedCustomerCodes.split(',').map(clearEmptySpaces).filter(Boolean);

      payload.excludedExternalCustomerIds = excludedCustomerCodes.join(',');
    }
  }

  if (payload.target === `${EBannerTarget.CUSTOMER_LIST}`) {
    const customerCodes = banner.customerCodes.split(',').map(clearEmptySpaces).filter(Boolean);

    payload.externalCustomerIds = customerCodes.join(',');
  }

  delete payload.applicationId;

  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    formData.append(key, value);
  });

  return formData;
};

export const toCreateBanner = (banner: TCreateBannerParams) => {
  const formData = toUpsertBanner(banner);

  return formData;
};

/**
 * As business rule, the backend service provides the image urls,
 * so we need to remove the blob files from the FormData
 * when updating a banner as long as the image file is not modified by a new blob file.
 */
export const toUpdateBanner = (banner: TUpdateBannerParams) => {
  const formData = toUpsertBanner(banner);

  if (!banner.mobileImageFile.blob) {
    formData.delete('mobileImageFile');
  }

  if (!banner.desktopImageFile.blob) {
    formData.delete('desktopImageFile');
  }

  if (banner.bannerType === `${EBannerType.POPUP}` && !banner.popupImageFile.blob) {
    formData.delete('popupImageFile');
  }

  return formData;
};

export const toToggleBanner = (banner: TToggleBannerParams) => {
  const formData = new FormData();

  formData.append('isActive', banner.isActive.toString());

  return formData;
};

export const toMapPublishSegmentedBannerListParams = (params: ISegmentedBannerListItem) => {
  const { zones, priceGroups } = params;

  const [zone] = zones;

  const [branch] = zone.branches;

  const [priceGroup] = priceGroups;

  const [priceList] = priceGroup.priceLists;

  const paramsDto: IPublishSegmentedBannersRequestDTO = {
    priceListId: priceList.priceListId,
    branch: {
      id: branch.branchId,
      distributorId: branch.distributor.distributorId,
    },
    banners: params.banners.map(
      (banner, index): IPublishBannerListItemDTO => ({
        id: banner.id,
        position: index + 1,
      }),
    ),
  };

  return paramsDto;
};
